import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import moment from "moment";
import VoucherService from "services/VoucherService";
import Utils from "utils";
import openNotification from "utils/notification";

export const getVoucherApi = createAsyncThunk(
  "voucher/getVoucherApi",
  async (data, { rejectWithValue }) => {
    try {
      const response = await VoucherService.voucherSearchGrid(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getVoucherById = createAsyncThunk(
  "voucher/getVoucherById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await VoucherService.getVoucherById(id);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getDetailVoucherApi = createAsyncThunk(
  "voucher/getDetailVoucher",
  async (id, { rejectWithValue }) => {
    try {
      const response = await VoucherService.getDetailVoucher(id);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const addVoucherApi = createAsyncThunk(
  "voucher/addVoucherApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await VoucherService.addVoucher(payload);
      if (onSuccess) onSuccess(response);
      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const updateVoucherApi = createAsyncThunk(
  "voucher/updateVoucherApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await VoucherService.updateVoucher(payload);
      if (onSuccess) onSuccess(response);
      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const delVoucherApi = createAsyncThunk(
  "voucher/delVoucherApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess, id } = data;
      const response = await VoucherService.delVoucher(id);
      if (onSuccess) onSuccess(response);
      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getVoucherUsageApi = createAsyncThunk(
  "voucher/getVoucherUsageApi",
  async (data, { rejectWithValue }) => {
    try {
      const response = await VoucherService.getVoucherUsageApi(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const voucherExportExcelApi = createAsyncThunk(
  "voucher/voucherExportExcelApi",
  async (data, { rejectWithValue }) => {
    try {
      const { code } = data;
      const payload = cloneDeep(data);
      delete payload.code;
      const blob = await VoucherService.voucherExportExcelApi(data);
      const url = window.URL.createObjectURL(blob);

      const exportTime = moment().format("DDMMYYYY_HHmmss");
      const filename = `Lich_su_su_dung_Voucher_${code}_${exportTime}.xlsx`;

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getVoucherOverviewStats = createAsyncThunk(
  "voucher/getVoucherOverviewStats",
  async (data, { rejectWithValue }) => {
    try {
      const response = await VoucherService.getVoucherOverviewStats(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getVoucherDailyTrendChart = createAsyncThunk(
  "voucher/getVoucherDailyTrendChart",
  async (data, { rejectWithValue }) => {
    try {
      const response = await VoucherService.getVoucherDailyTrendChart(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getVoucherRoiChart = createAsyncThunk(
  "voucher/getVoucherRoiChart",
  async (data, { rejectWithValue }) => {
    try {
      const response = await VoucherService.getVoucherRoiChart(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getVoucherLeaderboard = createAsyncThunk(
  "voucher/getVoucherLeaderboard",
  async (data, { rejectWithValue }) => {
    try {
      const response = await VoucherService.getVoucherLeaderboard(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

const initialState = {
  loading: false,
  voucherList: [],
  totalItems: 0,
  voucherDetail: {},
  listVoucherUsage: {},

  voucherStats: {},
  voucherDailyTrend: [],
  voucherRoi: [],
  voucherLeaderBoard: [],
};
export const voucherSlice = createSlice({
  name: "voucher",
  initialState,
  reducers: {
    showLoading: (state) => {
      state.loading = true;
    },
    setVoucherDetail: (state) => {
      state.loading = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getVoucherApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(getVoucherApi.fulfilled, (state, action) => {
        state.loading = false;
        state.voucherList = action.payload.items;
        state.totalItems = action.payload.totalItems;
      })
      .addCase(getVoucherApi.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getVoucherById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getVoucherById.fulfilled, (state, action) => {
        state.loading = false;
        state.voucherDetail = action.payload.voucherDetailDto;
        state.listVoucherUsage = action.payload.listVoucherUsage;
      })
      .addCase(getVoucherById.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(addVoucherApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(addVoucherApi.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addVoucherApi.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(updateVoucherApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateVoucherApi.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateVoucherApi.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(delVoucherApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(delVoucherApi.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(delVoucherApi.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getVoucherUsageApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(getVoucherUsageApi.fulfilled, (state, action) => {
        state.loading = false;
        state.listVoucherUsage = action.payload;
      })
      .addCase(getVoucherUsageApi.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(voucherExportExcelApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(voucherExportExcelApi.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(voucherExportExcelApi.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getDetailVoucherApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDetailVoucherApi.fulfilled, (state, action) => {
        state.loading = false;
        state.voucherDetail = action.payload.voucherDetailDto;
      })
      .addCase(getDetailVoucherApi.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(getVoucherOverviewStats.pending, (state) => {
        state.loading = true;
      })
      .addCase(getVoucherOverviewStats.fulfilled, (state, action) => {
        state.loading = false;
        state.voucherStats = action.payload;
      })
      .addCase(getVoucherOverviewStats.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(getVoucherDailyTrendChart.pending, (state) => {
        state.loading = true;
      })
      .addCase(getVoucherDailyTrendChart.fulfilled, (state, action) => {
        state.loading = false;
        state.voucherDailyTrend = action.payload;
      })
      .addCase(getVoucherDailyTrendChart.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(getVoucherRoiChart.pending, (state) => {
        state.loading = true;
      })
      .addCase(getVoucherRoiChart.fulfilled, (state, action) => {
        state.loading = false;
        state.voucherRoi = action.payload;
      })
      .addCase(getVoucherRoiChart.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(getVoucherLeaderboard.pending, (state) => {
        state.loading = true;
      })
      .addCase(getVoucherLeaderboard.fulfilled, (state, action) => {
        state.loading = false;
        state.voucherLeaderBoard = action.payload;
      })
      .addCase(getVoucherLeaderboard.rejected, (state, action) => {
        state.loading = false;
      });
  },
});

export const { showLoading, setVoucherDetail } = voucherSlice.actions;

export default voucherSlice.reducer;
